import React, { useEffect, useMemo, useState } from 'react'
import { WOOSMAP_PUBLIC_KEY } from '../../core/application/config'
import {
  DropOffLocationContext,
  useAppProviderContext,
  useConfigurationContext,
  useMapContext
} from '../../core/application/contexts'
import useFilter from '../../core/application/hooks/useFilter'
import { loadWoosmapMap, loadWoosmapMultisearch, loadWoosmapSdk } from '../../core/data/woosmap/woosmapSdk'
import { DropOffLocation } from '../../core/domain/models'
import { IDropOffProvider } from '../../core/domain/providers/DropOffProvider'

export interface IDropOffLocationManager {
  children?: React.ReactNode
}

/*
 * This formula was taken from https://groups.google.com/g/google-maps-js-api-v3/c/hDRO4oHVSeM/m/osOYQYXg2oUJ
 * At the time of development, Woosmap map has the same scales as Google Maps at all zoom level.
 */

const meterPerPixel = (zoom: number, latitude: number): number =>
  (156543.03392 * Math.cos((latitude * Math.PI) / 180)) / Math.pow(2, zoom)

const getRadius = (zoom: number, latitude: number): number => {
  const pixelRadius = 0.5 * Math.sqrt(window.innerHeight ** 2 + window.innerWidth ** 2)
  return pixelRadius * meterPerPixel(zoom, latitude)
}

const DropOffLocationManager: React.FC<IDropOffLocationManager> = ({ children }) => {
  const { tenantConfiguration, wasteTypeList } = useConfigurationContext()
  const currentWasteTypeIds = useMemo(() => wasteTypeList.map((wasteType) => wasteType.id), [wasteTypeList])
  const {
    setBrandProgram,
    dropOffLocation,
    setDropOffLocation,
    query,
    setWasteTypeIds,
    wasteTypeIds,
    buildContextualQuery
  } = useFilter(currentWasteTypeIds)

  const { position, zoom } = useMapContext()

  const { dropOffProvider, multisearchProvider } = useAppProviderContext()
  const [dropOffLocationList, setDropOffLocationList] = useState<Array<DropOffLocation>>([])
  const [woosmapSdkReady, setWoosmapSdkReady] = useState<boolean>(false)
  const [woosmapMapReady, setWoosmapMapReady] = useState<boolean>(false)
  const [queryLoading, setQueryLoading] = useState<boolean>(false)

  const [displaySubmitDropoffScreen, setDisplaySubmitDropoffScreen] = useState<boolean>(false)
  const [displayGetRewardDropoffId, setDisplayGetRewardDropoffId] = useState<string | undefined>(undefined)

  useEffect(() => {
    setQueryLoading(true)
  }, [query])

  useEffect(() => {
    if (tenantConfiguration && tenantConfiguration.brandProgram && tenantConfiguration.displayOnlyBrandProgramStores) {
      setBrandProgram(tenantConfiguration.brandProgram)
    }
  }, [setBrandProgram, tenantConfiguration])

  // Load Woosmap Map SDK
  useEffect(() => {
    const scriptId = 'woosmap-map-script-id'
    const loadMapSdk = async () => {
      await loadWoosmapMap(scriptId, WOOSMAP_PUBLIC_KEY, 'woosmapMapCallback')
    }
    window.woosmapMapCallback = function () {
      setWoosmapMapReady(true)
    }

    loadMapSdk()

    return () => {
      const tag = document.getElementById(scriptId)
      tag && tag.remove()
      setWoosmapMapReady(false)
    }
  }, [])

  useEffect(() => {
    const initWoosmapSdk = async (provider: IDropOffProvider) => {
      await loadWoosmapSdk('woosmap-script-id')
      if (window.WoosmapLoader) {
        provider.initWoosmap(WOOSMAP_PUBLIC_KEY, () => {
          setWoosmapSdkReady(true)
        })
      }
    }
    if (dropOffProvider && !woosmapSdkReady) {
      initWoosmapSdk(dropOffProvider)
    }
  }, [dropOffProvider, woosmapSdkReady])

  useEffect(() => {
    if (multisearchProvider && woosmapSdkReady) {
      loadWoosmapMultisearch('woosmap-multisearch-scrip-id').then(() => {
        multisearchProvider.init(WOOSMAP_PUBLIC_KEY, navigator.language)
      })
    }
  }, [woosmapSdkReady, multisearchProvider])

  useEffect(() => {
    const fetchDropOffLocation = async (provider: IDropOffProvider) => {
      const { lat, lng } = position
      const radius = Math.round(getRadius(zoom, lat))
      const data = await provider.fetchDropOffList({
        lat,
        lng,
        radius,
        query
      })
      setQueryLoading(false)
      setDropOffLocationList(data.items)
    }
    if (dropOffProvider && woosmapSdkReady) {
      fetchDropOffLocation(dropOffProvider)
    }
  }, [woosmapSdkReady, dropOffProvider, position, query, zoom])

  return (
    <DropOffLocationContext.Provider
      value={{
        dropOffLocationList,
        setDropOffLocationList,
        woosmapQuery: query,
        woosmapMapReady,
        currentDropOffLocation: dropOffLocation,
        setCurrentDropOffLocation: setDropOffLocation,
        wasteTypeFilters: wasteTypeIds,
        setWasteTypeFilters: setWasteTypeIds,
        displaySubmitDropoffScreen,
        setDisplaySubmitDropoffScreen,
        displayGetRewardDropoffId,
        setDisplayGetRewardDropoffId,
        buildContextualQuery,
        queryLoading
      }}
    >
      {children}
    </DropOffLocationContext.Provider>
  )
}

export default DropOffLocationManager
